import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Text, useThemeUI } from "theme-ui";
import filter from "lodash/fp/filter";
import find from "lodash/fp/find";
import flatMap from "lodash/fp/flatMap";
import flow from "lodash/fp/flow";
import get from "lodash/fp/get";
import getOr from "lodash/fp/getOr";
import isEmpty from "lodash/fp/isEmpty";
import map from "lodash/fp/map";
import mapValues from "lodash/fp/mapValues";
import merge from "lodash/fp/merge";
import negate from "lodash/fp/negate";
import pick from "lodash/fp/pick";
import reduce from "lodash/fp/reduce";
import some from "lodash/fp/some";
import toPairs from "lodash/fp/toPairs";
import uniq from "lodash/fp/uniq";
import values from "lodash/fp/values";

import { updateUserChoiceValues } from "store/app/appSlice";
import { selectUserChoiceValues } from "store/app";
import Button from "components/Button";
import getFieldNames from "./getFieldNames";
import getRequiredFields from "./getRequiredFields";
import Fields from "./Fields";
import { getFieldsWithIsDisabledValue } from "utils";
import * as styles from "./SkateCustomiseBlade.styles";

const mapValuesWithKey = mapValues.convert({ cap: false });

function getHasNoCustomisations({
  customizeBladePlayerName,
  customizeBladePlayerNumber,
  customizeBladeNoPersonalization,
  customizeBladeProfile,
  customizeBladeSharpening,
}) {
  return (
    ((!customizeBladePlayerName && !customizeBladePlayerNumber) ||
      customizeBladeNoPersonalization === true) &&
    customizeBladeProfile === "none" &&
    customizeBladeSharpening === "none"
  );
}

export default function SkateCustomiseBladeModalContent({
  title,
  bladeImage,
  bladeImageForPersonalisation,
  fields,
  cancelButtonLabel,
  customiseFeeNote = "",
  submitButtonLabel,
  close,
}) {
  const {
    theme: { isMobile },
  } = useThemeUI();

  const dispatch = useDispatch();

  const userChoiceValues = useSelector(selectUserChoiceValues);

  const fallBackFields = {
    customizeBladePlayerName: "playerName",
    customizeBladePlayerNumber: "playerNumber",
  };

  const fieldNames = getFieldNames(fields);

  const initialState = fieldNames.reduce(
    (acc, name) => ({
      ...acc,
      [name]:
        userChoiceValues[name] ||
        userChoiceValues[fallBackFields[name] || "__"] ||
        "",
    }),
    {}
  );

  const [fieldsState, setFieldsState] = useState(initialState);

  const hasNoCustomisations = getHasNoCustomisations(fieldsState);

  const fieldsWithIsDisabledValues =
    getFieldsWithIsDisabledValue(fieldsState)(fields);

  const { bladeType } = bladeImageForPersonalisation;
  const isCarbonLite = bladeType === "carbonLite";

  const hasBladePlayerNumber = Boolean(fieldsState?.customizeBladePlayerNumber);

  const hasRequiredValues = flow(
    getRequiredFields(fieldsState),
    map((requiredFields) => {
      return flow(
        getOr({}, "validation"),
        mapValues(
          flow(
            mapValuesWithKey((validationType, fieldName) => {
              if (validationType === "isEmpty") {
                return isEmpty(fieldsState[fieldName]);
              }
              return false;
            }),
            some(Boolean)
          )
        ),
        merge(requiredFields)
      )(requiredFields);
    }),
    filter(negate(get("isDisabled"))),
    filter(get("isRequired")),
    map(get("name")),
    (requiredFields) => pick(requiredFields)(fieldsState),
    values,
    filter((value) => typeof value === "string" && isEmpty(value)),
    (emptyFields) => emptyFields.length === 0
  )(fieldsWithIsDisabledValues);

  const isValid = !hasNoCustomisations && hasRequiredValues;

  const handleChange = (name) => (value) => {
    setFieldsState((currentFieldsState) => {
      return flow(
        find((field) => field.name === name),
        getOr([], ["options"]),
        flatMap(getOr([], "fields")),
        map(get("name")),
        uniq,
        reduce((acc, curr) => ({ ...acc, [curr]: "" }), {
          ...currentFieldsState,
          [name]: value,
        })
      )(fields);
    });
  };

  const dispatchUpdateUserChoiceValues = () => {
    dispatch(
      updateUserChoiceValues(
        flow(
          toPairs,
          map(([name, value]) => ({ name, value }))
        )(fieldsState)
      )
    );
    close();
  };

  const handleSubmit = (evt) => {
    evt.preventDefault();
    if (isValid) {
      dispatchUpdateUserChoiceValues();
    }
  };

  return (
    <Box
      as="form"
      sx={styles.modalContent}
      onSubmit={handleSubmit}
      autoComplete="off"
    >
      {customiseFeeNote && (
        <Text
          sx={styles.customiseFeeNote}
          variant="h5Light"
          dangerouslySetInnerHTML={{ __html: customiseFeeNote }}
        />
      )}

      <Box sx={styles.header}>
        <Text
          dangerouslySetInnerHTML={{ __html: title }}
          variant={isMobile ? "h3Light" : "h2Light"}
        />
        {!isMobile && <Box sx={styles.bladeImage({ bladeImage })} />}
      </Box>

      <Box sx={styles.headerImageContainer}>
        <Box sx={styles.headerImage({ bladeImageForPersonalisation })} />
        <Box sx={styles.bladeTextContainer({ bladeType })}>
          <Box
            sx={
              isCarbonLite
                ? styles.carbonLiteBladePersonalisationNameText({
                    hasBladePlayerNumber,
                  })
                : styles.defaultBladePersonalisationNameText({
                    hasBladePlayerNumber,
                  })
            }
          >
            {fieldsState?.customizeBladePlayerName || ""}
          </Box>

          <Box>{fieldsState?.customizeBladePlayerNumber || ""}</Box>
        </Box>
      </Box>

      <Fields
        onChange={handleChange}
        fields={fieldsWithIsDisabledValues}
        fieldsState={fieldsState}
      />

      <Box sx={styles.actions}>
        <Button onClick={close} isRound size="large">
          {cancelButtonLabel}
        </Button>

        <Button type="submit" isRound size="large" disabled={!isValid}>
          {submitButtonLabel}
        </Button>
      </Box>
    </Box>
  );
}
